import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AuthUser } from '../../types';

/** Tenant en el que está "actuando" el staff de plataforma (platform_super/support).
 *  Incluye el contexto del tenant (plan/branding) para pintar SU CRM en modo soporte. */
export interface ActingTenant {
  id: string;
  slug: string;
  name: string;
  mode: 'native' | 'booster';
  /** País del tenant, ISO-2. Para el sello de plataforma del sidebar. */
  country?: string;
  /** Features del plan del tenant (para gatear el menú por plan). */
  features?: string[];
  /** true si el tenant es nativo (Imperium) → acceso full de plan. */
  native?: boolean;
  /** Branding white-label del tenant (logo/color/nombre) para la topbar. */
  branding?: { logoUrl?: string; primaryColor?: string; displayName?: string } | null;
  /** Identidad emulada del admin del tenant (tenant_admin): el menú se arma con
   *  este rol + permisos, para que calce exacto con lo que ve el admin al loguearse. */
  role?: string;
  permissions?: string[];
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  actingTenant: ActingTenant | null;
  /** false hasta que se resolvió el intento de restaurar sesión al arrancar la app
   *  (refresh silencioso con la cookie). Evita que AuthGuard tire a /login antes de
   *  tiempo en un deep-link abierto en frío (pestaña nueva / link de Gustavo). */
  bootstrapped: boolean;
}

const initialState: AuthState = {
  user: null,
  accessToken: null,
  isAuthenticated: false,
  actingTenant: null,
  bootstrapped: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials(state, action: PayloadAction<{ user: AuthUser; accessToken: string }>) {
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.isAuthenticated = true;
      state.bootstrapped = true;
    },
    tokenRefreshed(state, action: PayloadAction<string>) {
      state.accessToken = action.payload;
    },
    /** Marca que ya terminó el intento de restaurar sesión al arrancar (haya o no
     *  sesión). A partir de acá AuthGuard puede decidir: render o /login. */
    authBootstrapped(state) {
      state.bootstrapped = true;
    },
    logout(state) {
      state.user = null;
      state.accessToken = null;
      state.isAuthenticated = false;
      state.actingTenant = null;
      state.bootstrapped = true;
    },
    updateUser(state, action: PayloadAction<Partial<AuthUser>>) {
      if (state.user) Object.assign(state.user, action.payload);
    },
    setActingTenant(state, action: PayloadAction<ActingTenant | null>) {
      state.actingTenant = action.payload;
    },
  },
});

export const { setCredentials, tokenRefreshed, authBootstrapped, logout, updateUser, setActingTenant } = authSlice.actions;
export default authSlice.reducer;
